import { Map, fromJS } from 'immutable'
import type { Avatar } from '../../../types'
import { isStringMap } from './state-types'
import * as actions from './actions'

const AVATAR_CACHE_MAX = 500

type CommunityProfileAction =
  | { type: typeof actions.SET_COMMUNITY_PROFILE_AVATAR_UPLOAD_PROCESSING; payload: boolean }
  | { type: typeof actions.SET_COMMUNITY_PROFILE_AVATAR_UPLOAD_SUCCESS }
  | { type: typeof actions.SET_COMMUNITY_PROFILE_AVATAR_UPLOAD_ERROR; payload: string }
  | { type: typeof actions.SET_COMMUNITY_PROFILE_AVATAR_DELETE_PROCESSING; payload: boolean }
  | { type: typeof actions.SET_COMMUNITY_PROFILE_AVATAR_DELETE_SUCCESS }
  | { type: typeof actions.SET_COMMUNITY_PROFILE_AVATAR_DELETE_ERROR; payload: string }
  | { type: typeof actions.SET_COMMUNITY_PROFILE_PICTURES; payload: Record<string, Avatar> }
  | { type: typeof actions.SET_COMMUNITY_PROFILE_PICTURES_PROCESSING; payload: boolean }
  | { type: typeof actions.SET_COMMUNITY_PROFILE_PICTURES_ERROR; payload: string }
  | { type: string; payload?: never }

export const initialState = Map({
  profilePictures: Map({
    data: Map(),
    processing: false,
    error: null,
  }),
  avatarUpload: Map({
    processing: false,
    error: null,
    success: false,
  }),
  avatarDelete: Map({
    processing: false,
    error: null,
    success: false,
  }),
})

export default function reducer (
  state = initialState,
  action: CommunityProfileAction,
) {
  switch (action.type) {
    case actions.SET_COMMUNITY_PROFILE_AVATAR_UPLOAD_PROCESSING:
      return state.setIn(['avatarUpload', 'processing'], action.payload)

    case actions.SET_COMMUNITY_PROFILE_AVATAR_UPLOAD_SUCCESS:
      return state
        .setIn(['avatarUpload', 'success'], true)
        .setIn(['avatarUpload', 'error'], null)
        .setIn(['avatarUpload', 'processing'], false)

    case actions.SET_COMMUNITY_PROFILE_AVATAR_UPLOAD_ERROR:
      return state
        .setIn(['avatarUpload', 'error'], action.payload)
        .setIn(['avatarUpload', 'success'], false)
        .setIn(['avatarUpload', 'processing'], false)

    case actions.SET_COMMUNITY_PROFILE_AVATAR_DELETE_PROCESSING:
      return state.setIn(['avatarDelete', 'processing'], action.payload)

    case actions.SET_COMMUNITY_PROFILE_AVATAR_DELETE_SUCCESS:
      return state
        .setIn(['avatarDelete', 'success'], true)
        .setIn(['avatarDelete', 'error'], null)
        .setIn(['avatarDelete', 'processing'], false)

    case actions.SET_COMMUNITY_PROFILE_AVATAR_DELETE_ERROR:
      return state
        .setIn(['avatarDelete', 'error'], action.payload)
        .setIn(['avatarDelete', 'success'], false)
        .setIn(['avatarDelete', 'processing'], false)

    case actions.SET_COMMUNITY_PROFILE_PICTURES: {
      // fromJS returns the wide JS-to-Immutable union; we know the action
      // payload is a flat string-keyed object, so this is the single
      // permitted Immutable boundary cast in the reducer.
      const incoming = fromJS(action.payload || {}) as Map<string, unknown>
      const existing = state.getIn(['profilePictures', 'data'])
      const previous = isStringMap(existing) ? existing : Map<string, unknown>()
      const merged = previous.merge(incoming)
      // FIFO eviction: drops the earliest insertion-order entries once the cap
      // is exceeded. Immutable.Map preserves insertion order, so .slice drops
      // the oldest entries (not the least-recently-used ones).
      const trimmed = merged.size > AVATAR_CACHE_MAX
        ? merged.slice(merged.size - AVATAR_CACHE_MAX)
        : merged
      return state
        .setIn(['profilePictures', 'data'], trimmed)
        .setIn(['profilePictures', 'error'], null)
    }

    case actions.SET_COMMUNITY_PROFILE_PICTURES_PROCESSING:
      return state.setIn(['profilePictures', 'processing'], action.payload)

    case actions.SET_COMMUNITY_PROFILE_PICTURES_ERROR:
      return state.setIn(['profilePictures', 'error'], action.payload)

    default:
      return state
  }
}
